import { useSEO } from "@/components/SEO";
import { Eyebrow, Masked, Reveal, PrimaryCTA } from "@/components/bits";
import CTASection from "@/components/CTASection";
import { SEO, ASSETS, IMAGES } from "@/lib/site";

const DIFFERENT = [
    "We diagnose economics before recommending activity.",
    "We operate the system instead of simply handing over software.",
    "We look beyond lead generation to conversion, recovery, productivity, margin, and cost reduction.",
    "We can bring cost-reduction partners and a 150+ vendor network into the Business X-Ray when savings opportunities are identified.",
    "We measure business outcomes: revenue, gross profit, efficiency, recovered revenue, and savings.",
    "We use AI for leverage while preserving human judgment for critical decisions.",
];

const About = () => {
    useSEO({ title: SEO["/about"].title, description: SEO["/about"].description, path: "/about" });

    return (
        <>
            <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20" data-testid="about-hero">
                <div className="bg-grid-light absolute inset-0 [mask-image:radial-gradient(70%_60%_at_30%_20%,black,transparent)]" aria-hidden="true" />
                <div className="container-x relative">
                    <Eyebrow>About Vantera</Eyebrow>
                    <Masked
                        lines={["Revenue Growth Should Not Require", "Cost to Grow", "at the Same Rate."]}
                        className="mt-7 max-w-5xl font-display text-4xl font-extrabold leading-[1.06] tracking-tightest sm:text-6xl lg:text-[68px]"
                        itemClassName="[&:nth-child(2)]:text-outline"
                    />
                    <div className="mt-12 grid gap-12 lg:grid-cols-[1.3fr_1fr]">
                        <Reveal delay={0.2}>
                            <p className="max-w-2xl text-lg leading-relaxed text-slategray sm:text-xl">
                                Vantera was built around a simple idea: established companies already possess enormous growth assets. The opportunity is to make those assets work together more intelligently.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="about-assets">
                                {ASSETS.map((a, i) => (
                                    <Reveal key={a} delay={0.2 + i * 0.04}>
                                        <span className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 text-[14px] font-medium text-ink">
                                            <span className="size-1.5 rotate-45 bg-vantera" aria-hidden="true" /> {a}
                                        </span>
                                    </Reveal>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <div className="overflow-hidden rounded-2xl">
                                <img src={IMAGES.about} alt="Modern corporate architecture — glass towers" className="h-[340px] w-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="relative overflow-hidden bg-ink py-24 text-ivory sm:py-32" data-testid="about-mission">
                <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
                <div className="container-x relative">
                    <div className="grid gap-12 lg:grid-cols-[0.6fr_1.4fr]">
                        <p className="font-mono text-xs uppercase tracking-[0.24em] text-cloud">Our mission</p>
                        <div>
                            <Masked inview lines={["Build Smarter, More Profitable", "Revenue Organizations."]} className="font-display text-4xl font-extrabold leading-[1.1] tracking-tightest sm:text-6xl" />
                            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-cloud sm:text-xl">
                                We combine data, AI, automation, marketing, sales execution, revenue recovery, margin intelligence, and cost reduction into a measurable operating system.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-24 sm:py-32" data-testid="about-different">
                <div className="container-x grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
                    <div className="lg:sticky lg:top-28 lg:self-start">
                        <Eyebrow>What makes Vantera different</Eyebrow>
                        <Masked inview lines={["Diagnosis Before", "Activity. Outcomes", "Before Activity Metrics."]} className="mt-7 font-display text-3xl font-extrabold tracking-tightest sm:text-4xl" />
                    </div>
                    <div>
                        {DIFFERENT.map((d, i) => (
                            <Reveal key={i} delay={i * 0.05}>
                                <div className="flex gap-6 border-t border-ink/10 py-6 last:border-b" data-testid={`about-different-${i + 1}`}>
                                    <span className="font-mono text-xs tracking-[0.2em] text-vantera">{String(i + 1).padStart(2, "0")}</span>
                                    <p className="max-w-xl text-[17px] font-medium leading-relaxed text-ink">{d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-ink/10 bg-paper py-20 sm:py-24" data-testid="about-goal">
                <div className="container-x flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
                    <div>
                        <Eyebrow>Our goal</Eyebrow>
                        <p className="mt-5 max-w-2xl font-display text-2xl font-extrabold leading-snug tracking-tight sm:text-3xl">
                            More profitable revenue and a more efficient cost structure — from the organization you already have.
                        </p>
                    </div>
                    <PrimaryCTA to="/contact" testid="about-goal-cta">Talk to Vantera</PrimaryCTA>
                </div>
            </section>

            <CTASection heading={["Build a More Profitable", "Business. Not Another Campaign."]} sub="Vantera — the revenue operating system for established B2B companies." primary="Request a Business X-Ray" secondary="Book an Executive Review" testid="about-final-cta" />
        </>
    );
};

export default About;